import { ActivityType, EventType } from 'types/models';
import {
    Badge,
    Box,
    Button,
    Flex,
    Grid,
    HStack,
    Icon,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Progress,
    Text,
    UseToastOptions,
    VStack,
    useToast
} from '@chakra-ui/react';
import { CheckIcon, TimeIcon } from '@chakra-ui/icons';
import React, { useState } from 'react';
import { getDate, renderDateAsTime, renderDateAsDayMonth } from 'utils/dateUtils';

import { GenericFrontError } from 'errors/GenericFrontError';
import { ResponseError } from 'errors/ResponseError';
import StarRatingComponent from 'react-star-rating-component';
import TOAST from 'constants/TOAST';
import { api } from 'services/axios';
import getActivityCode from 'utils/getActivityCode';
import { useAuth } from 'hooks/auth';

interface ScheduleActivityCardProps {
    event: EventType;
    activity: ActivityType;
    alreadyRegistered: boolean;
    refreshActivities: () => void;
}

const getCategoryColor = (categoryName: string): string => {
    const lowerName = categoryName.toLowerCase();
    if (lowerName.includes('minicurso')) return 'blue';
    if (lowerName.includes('palestra')) return 'purple';
    if (lowerName.includes('oficina') || lowerName.includes('workshop')) return 'orange';
    if (lowerName.includes('mesa')) return 'cyan';
    if (lowerName.includes('painel')) return 'teal';
    if (lowerName.includes('apresentação') || lowerName.includes('apresentacao')) return 'pink';
    return 'gray';
};

const getProgressBarColor = (ratio: number): string => {
    const r = Math.min(1, Math.max(0, ratio));

    // Mantém verde/amarelo por mais tempo
    // Só chega no vermelho perto de 100%
    const hue = 120 - (r ** 2.2) * 120;

    return `hsl(${hue}, 85%, 50%)`;
};


const ScheduleActivityCard: React.FC<ScheduleActivityCardProps> = ({
    activity,
    alreadyRegistered,
    event,
    refreshActivities,
}) => {
    const { user } = useAuth();
    const toastId = 'schedule-activity-card';
    const toast = useToast({
        id: toastId,
        duration: TOAST.DURATION,
        position: TOAST.DEFAULT_POSITION,
        isClosable: TOAST.CLOSABLE_BY_DEFAULT,
    });

    const toaster = (options: UseToastOptions) => {
        if (toast.isActive(toastId)) {
            toast.close(toastId);
            setTimeout(() => toast(options), 150);
        } else {
            toast(options);
        }
    };

    const [submitIsLoading, setSubmitIsLoading] = useState(false);

    // We compute local state directly from props to avoid race conditions.
    const isRegistered = alreadyRegistered;
    const amountRegistered = activity.totalRegistry ? activity.totalRegistry : 0;
    const rating = activity.activityRegistration ? activity.activityRegistration[0].rating : 0;
    const isSoldOut = amountRegistered >= activity.vacancy;
    const progressPercent = activity.vacancy > 0 ? Math.min(100, (amountRegistered / activity.vacancy) * 100) : 0;

    const [conflictModalOpen, setConflictModalOpen] = useState(false);
    const [conflictData, setConflictData] = useState<{ message: string } | null>(null);

    const onConfirmConflict = async () => {
        if (!user) return;
        setSubmitIsLoading(true);
        setConflictModalOpen(false);
        api.post(`/activity/registry/${activity.id}/${user.id}?force=true`)
            .then(() => {
                toaster({ title: 'Inscrito com sucesso!', status: 'success' });
                refreshActivities();
            })
            .catch(() => {
                toaster({ title: 'Erro inesperado ao forçar inscrição', status: 'error' });
            })
            .finally(() => {
                setSubmitIsLoading(false);
                setConflictData(null);
            });
    };

    const userInscription = (activityId: number) => {
        if (!user) {
            toaster({ title: 'Faça o login para se inscrever', status: 'warning' });
            return;
        }
        setSubmitIsLoading(true);
        api.post(`/activity/registry/${activityId}/${user.id}`)
            .then(() => {
                toaster({ title: 'Inscrito!', status: 'success' });
                refreshActivities();
            })
            .catch((err) => {
                let message = 'Erro inesperado';
                if (err instanceof GenericFrontError) {
                    message = err.message;
                    if (err instanceof ResponseError && err.status === 409) {
                        const activityName = err.response?.data?.[0]?.activityName;
                        setConflictData({
                            message: `Você já está inscrito na atividade "${activityName}" que ocorre no mesmo horário desta. Deseja sair dessa atividade e se inscrever nesta?`
                        });
                        setConflictModalOpen(true);
                        return;
                    } else if (err instanceof ResponseError && err.status === 400) {
                        message = err.response?.message || 'Você é responsável ou ministra essa atividade';
                    }
                }
                toaster({ title: message, status: 'error' });
            })
            .finally(() => setSubmitIsLoading(false));
    };

    const unregisterUser = (activityId: number) => {
        if (user) {
            setSubmitIsLoading(true);
            api.delete(`/activity/registry/${activityId}/${user.id}`)
                .then(() => {
                    toaster({ title: 'Inscrição removida!', status: 'success' });
                    refreshActivities();
                })
                .catch((err) => {
                    let message = 'Erro inesperado';
                    if (err instanceof GenericFrontError) message = err.message;
                    toaster({ title: message, status: 'error' });
                })
                .finally(() => setSubmitIsLoading(false));
        }
    };

    // We find the schedule that matches the current day if possible, otherwise use the first one.
    // For now, keeping it simple as the parent filters by schedules[0]
    const firstSchedule = activity.schedules?.[0];
    const startTimeStr = firstSchedule ? renderDateAsTime(firstSchedule.startDate) : '--:--';
    const endTimeObj = firstSchedule ? new Date(new Date(firstSchedule.startDate).getTime() + firstSchedule.durationInMinutes * 60000) : null;
    const endTimeStr = endTimeObj ? renderDateAsTime(endTimeObj) : '--:--';

    // Check if subscription period is open
    const isSubscriptionOpen = event && new Date(event.registryStartDate) <= getDate() && new Date(event.registryEndDate) >= getDate();

    const categoryColor = getCategoryColor(activity.activityCategory.description);
    console.log(activity);

    return (
        <Flex
            direction="column"
            bg="white"
            borderRadius="xl"
            boxShadow="md"
            borderWidth="1px"
            borderColor={isRegistered ? 'blue.400' : 'gray.200'}
            overflow="hidden"
            transition="all 0.3s"
            _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg' }}
            h="100%"
            as="article"
        >
            <Modal isOpen={conflictModalOpen} onClose={() => setConflictModalOpen(false)}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Conflito de Horário</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>{conflictData?.message}</ModalBody>
                    <ModalFooter>
                        <Button colorScheme="green" mr={3} isLoading={submitIsLoading} onClick={onConfirmConflict}>Sim</Button>
                        <Button colorScheme="red" isDisabled={submitIsLoading} onClick={() => setConflictModalOpen(false)}>Não</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Cabeçalho do Card (Horário e Tags) */}
            <Flex
                bg="gray.50"
                px={6}
                py={4}
                borderBottomWidth="1px"
                borderColor="gray.100"
                justify="space-between"
                align="center"
                wrap="wrap"
                gap={2}
            >
                <HStack color="gray.700" fontWeight="semibold">
                    <Icon as={TimeIcon} color="gray.400" boxSize={5} />
                    <Text fontSize="2xl">{startTimeStr} - {endTimeStr}</Text>
                </HStack>
                <HStack>
                    <Badge colorScheme="blue" px={2} py={1} borderRadius="md" fontSize="md">
                        {activity.workloadInMinutes} min
                    </Badge>
                    <Badge colorScheme={categoryColor} px={3} py={1} borderRadius="full" textTransform="uppercase" fontSize="lg" fontWeight="bold" letterSpacing="wider">
                        {activity.activityCategory.description}
                    </Badge>
                    <Badge bg="gray.200" color="gray.600" px={2} py={1} borderRadius="md" fontFamily="mono" fontSize="xl" textTransform="none">
                        #{getActivityCode(activity)}
                    </Badge>
                </HStack>
            </Flex>

            {/* Corpo do Card */}
            <Flex direction="column" p={6} flex="1">
                <Text fontSize="2xl" fontWeight="bold" color="gray.900" mb={4} lineHeight="tight">
                    {activity.title}
                </Text>

                <Text fontSize="2xl" color="gray.600" mb={6} flexGrow={1} noOfLines={4} title={activity.description}>
                    {activity.description}
                </Text>

                {/* Informações Detalhadas (Palestrantes, Local) */}
                <Box bg="gray.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="gray.100" mb={6} fontSize="xl">
                    <VStack align="stretch" spacing={2}>
                        {activity.teachingUsers && activity.teachingUsers.length > 0 && (
                            <Grid templateColumns="110px 1fr" gap={2}>
                                <Text fontWeight="semibold" color="gray.700">Ministrantes:</Text>
                                <Text color="gray.600">{activity.teachingUsers.map(u => u.name).join(', ')}</Text>
                            </Grid>
                        )}
                        {activity.responsibleUsers && activity.responsibleUsers.length > 0 && (
                            <Grid templateColumns="110px 1fr" gap={2}>
                                <Text fontWeight="semibold" color="gray.700">Responsáveis:</Text>
                                <Text color="gray.600">{activity.responsibleUsers.map(u => u.name).join(', ')}</Text>
                            </Grid>
                        )}
                        {activity.schedules && activity.schedules.length > 0 && (
                            <Grid templateColumns="110px 1fr" gap={2} alignItems="start">
                                <Text fontWeight="semibold" color="gray.700">Horários:</Text>
                                <VStack align="stretch" spacing={2} fontSize="xl">
                                    {activity.schedules.map((schedule, idx) => {
                                        const start = new Date(schedule.startDate);
                                        const end = new Date(start.getTime() + schedule.durationInMinutes * 60000);
                                        return (
                                            <Box key={idx} bg="white" px={3} py={2} borderRadius="md" borderWidth="1px" borderColor="gray.200" shadow="sm">
                                                <Text fontWeight="semibold" color="gray.800">
                                                    {renderDateAsDayMonth(start)} das {renderDateAsTime(start)} às {renderDateAsTime(end)}
                                                </Text>
                                                {schedule.room && (
                                                    <Text fontSize="lg" color="gray.600" mt={1}>
                                                        Local: <Text as="span" fontWeight="semibold" color="gray.800">{schedule.room.code}</Text>
                                                    </Text>
                                                )}
                                                {schedule.url && (
                                                    <Text fontSize="lg" color="gray.600" mt={1}>
                                                        Sala Virtual: <Text as="a" color="blue.500" href={schedule.url} target="_blank">{schedule.url}</Text>
                                                    </Text>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            </Grid>
                        )}
                    </VStack>
                </Box>

                {/* Rating Section se aplicável */}
                {activity.activityRegistration && (
                    <Box mb={6}>
                        <Text fontSize="xl" fontWeight="bold" color="gray.600" mb={1}>
                            Sua Avaliação {rating === 0 && <Text as="span" color="orange.400">(Pendente)</Text>}
                        </Text>
                        <Box fontSize="3xl" lineHeight="1">
                            <StarRatingComponent
                                name={`rating-${activity.id}`}
                                value={rating}
                                onStarClick={(next) => {
                                    if (user) {
                                        api.put(`/activity/${activity.id}/rate/${user.id}`, { rating: next })
                                            .then(() => refreshActivities())
                                            .catch((err) => toaster({ title: 'Erro ao avaliar', description: `${err}`, status: 'error' }));
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                )}

                {/* Vagas e Ação */}
                <Box mt="auto" pt={4} borderTopWidth="1px" borderColor="gray.100">
                    {activity.vacancy > 0 && (
                        <Box mb={4}>
                            <Flex justify="space-between" fontSize="xl" mb={2}>
                                <Text color="gray.500">Vagas Ocupadas</Text>
                                <Text fontFamily="mono" color="gray.700" fontWeight="semibold">
                                    {amountRegistered} / {activity.vacancy}
                                </Text>
                            </Flex>
                            <Progress
                                value={progressPercent}
                                size="sm"
                                borderRadius="full"
                                bg="gray.200"
                                sx={{
                                    '& > div': {
                                        backgroundColor: getProgressBarColor(amountRegistered / activity.vacancy),
                                        transition: 'background-color 0.3s ease, width 0.3s ease',
                                    }
                                }}
                            />
                        </Box>
                    )}

                    {isSubscriptionOpen ? (
                        isRegistered ? (
                            <HStack w="100%">
                                <Badge colorScheme="blue" fontSize="xl" py={2} borderRadius="lg" flex="1" textAlign="center">
                                    INSCRITO
                                </Badge>
                                <Button
                                    colorScheme="red"
                                    variant="outline"
                                    isLoading={submitIsLoading}
                                    onClick={() => unregisterUser(activity.id)}
                                    borderRadius="lg"
                                >
                                    Cancelar
                                </Button>
                            </HStack>
                        ) : isSoldOut ? (
                            <Badge colorScheme="red" fontSize="xl" py={3} borderRadius="lg" w="100%" textAlign="center">
                                ESGOTADO
                            </Badge>
                        ) : (
                            <Button
                                colorScheme="green"
                                w="100%"
                                isLoading={submitIsLoading}
                                onClick={() => userInscription(activity.id)}
                                _hover={{ bg: 'green.600' }}
                                bg="green.500"
                                color="white"
                                fontWeight="semibold"
                                fontSize="xl"
                                py={6}
                                borderRadius="lg"
                                leftIcon={<CheckIcon />}
                            >
                                Inscrever-se
                            </Button>
                        )
                    ) : (
                        <Text color="gray.500" fontSize="xl" textAlign="center" fontWeight="bold">
                            Inscrições Fechadas
                        </Text>
                    )}
                </Box>
            </Flex>
        </Flex>
    );
};

export default ScheduleActivityCard;
